import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { BaseModal } from "../components/modals/BaseModal";
import { useEffect, useState } from "react";
import { handleApi } from "../utils/handleApi";
import { createCreditPurchaseLink } from "../services/payment/Credits";
import toast from "react-hot-toast";
import { membershipStatsAxios } from "../services/membership/MembershipAxios";
import { format } from "date-fns";
import { convertToZone } from "../utils/formatters";
import { useAuth } from "../context/AuthContext";
import { dictionary } from "../utils/dictionary";

const getRedirectPath = (defaultPath: string): string => {
  const isMobile = window.innerWidth < 768;
  if (defaultPath === "/home" && isMobile) {
    return "/settings";
  }
  return defaultPath;
};

interface PaymentScenario {
  imageSrc: string;
  titleKey: string;
  descriptionKey: string;
  buttonOne: {
    textKey: string;
    redirectTo: string;
  };
  buttonTwo?: {
    textKey: string;
    redirectTo: string;
  };
}

type PaymentResultType = "subscription" | "credits" | "order";

const paymentConfig: Record<"success" | "cancel", Record<PaymentResultType, PaymentScenario>> = {
  success: {
    subscription: {
      imageSrc: "/gif/Success.gif",
      titleKey: "payment-successful",
      descriptionKey: "subscription-success-description",
      buttonOne: {
        textKey: "continue",
        redirectTo: "/home"
      }
    },
    credits: {
      imageSrc: "https://fluxi.s3.amazonaws.com/prod/assets/media/green-card.webm",
      titleKey: "credits-purchased-title",
      descriptionKey: "credits-success-description",
      buttonOne: {
        textKey: "continue",
        redirectTo: "/membership"
      }
    },
    order: {
      imageSrc: "/gif/Success.gif",
      titleKey: "payment-successful",
      descriptionKey: "order-payment-success-description",
      buttonOne: {
        textKey: "close",
        redirectTo: "/"
      }
    }
  },
  cancel: {
    subscription: {
      imageSrc: "/gif/delete_page.gif",
      titleKey: "payment-failed",
      descriptionKey: "payment-failed-description",
      buttonOne: {
        textKey: "try-again",
        redirectTo: "/plans"
      },
      buttonTwo: {
        textKey: "cancel",
        redirectTo: "/home"
      }
    },
    credits: {
      imageSrc: "https://fluxi.s3.amazonaws.com/prod/assets/media/red-card.webm",
      titleKey: "purchase-cancelled",
      descriptionKey: "credits-cancel-description",
      buttonOne: {
        textKey: "try-again",
        redirectTo: "/membership"
      },
      buttonTwo: {
        textKey: "cancel",
        redirectTo: "/home"
      }
    },
    order: {
      imageSrc: "https://fluxi.s3.amazonaws.com/prod/assets/media/red-card.webm",
      titleKey: "purchase-cancelled",
      descriptionKey: "order-payment-cancel-description",
      buttonOne: {
        textKey: "close",
        redirectTo: "/"
      }
    }
  }
};

export const PaymentResultPage = () => {
  const { status } = useParams<{ status: "success" | "cancel" }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useAuth();
  const [isOpen, setIsOpen] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);
  const [endMembershipDateFormatted, setEndMembershipDateFormatted] = useState("");
  const dateFormat = "dd/MM/yyyy";

  const getStats = async () => {
    try {
      const stats = await membershipStatsAxios();

      const formattedDate = convertToZone(
        format(new Date(stats.active_membership.ends_at || ""), dateFormat),
        dateFormat
      )

      setEndMembershipDateFormatted(formattedDate);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const creditsAmount = searchParams.get("credits");
  const requestedType = searchParams.get("type");
  const type: PaymentResultType = requestedType === "order"
    ? "order"
    : creditsAmount
      ? "credits"
      : "subscription";

  useEffect(() => {
    if (type === "credits") {
      getStats();
    }
  }, [type]);
  
  const planName = searchParams.get("plan") || "Premium";
  const validStatus = (status || "success") as "success" | "cancel";
  
  const config = paymentConfig[validStatus]?.[type] || paymentConfig.success.subscription;
  
  const title = dictionary[config.titleKey]?.[language] || config.titleKey;
  const buttonOneText = dictionary[config.buttonOne.textKey]?.[language] || config.buttonOne.textKey;
  const buttonTwoText = config.buttonTwo 
    ? dictionary[config.buttonTwo.textKey]?.[language] || config.buttonTwo.textKey 
    : undefined;
  
  let description = dictionary[config.descriptionKey]?.[language] || config.descriptionKey;
  if (type === "subscription") {
    description = description.replace("[plan_name]", planName);
  } else if (type === "credits") {
    description = description.replace(/\[credits_amount\]/g, creditsAmount || "0");
    description = description.replace("[expiration_date]", endMembershipDateFormatted || "...");
  } else {
    description = description.replace(
      "[order_code]",
      searchParams.get("order_code") || searchParams.get("order_id") || ""
    );
  }

  useEffect(() => {
    if (!status || !["success", "cancel"].includes(status)) {
      navigate(getRedirectPath("/home"));
    }
  }, [status, navigate]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      navigate(getRedirectPath(config.buttonOne.redirectTo));
    }, 200);
  };

  const handleRetryPurchase = async () => {
    if (!creditsAmount) return;
    
    setIsRetrying(true);
    const { res } = await handleApi(createCreditPurchaseLink(parseInt(creditsAmount)));
    
    if (res && res.payment_url) {
      window.location.href = res.payment_url;
    } else {
      setIsRetrying(false);
      toast.error(dictionary["error-creating-purchase-link"]?.[language] || "Error creating purchase link");
    }
  };

  const handleButtonOne = () => {
    if (type === "order") {
      window.close();
      return;
    }
    if (validStatus === "cancel") {
      if (type === "credits") {
        handleRetryPurchase();
      } else {
        navigate(getRedirectPath(config.buttonOne.redirectTo));
      }
    } else {
      navigate(getRedirectPath(config.buttonOne.redirectTo));
    }
  };

  const handleButtonTwo = () => {
    if (config.buttonTwo) {
      navigate(getRedirectPath(config.buttonTwo.redirectTo));
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      description={description}
      imageSrc={config.imageSrc}
      imageAlt={status === "success" 
        ? dictionary["payment-successful"]?.[language] || "Payment successful" 
        : dictionary["payment-failed"]?.[language] || "Payment failed"}
      buttonOne={{
        text: buttonOneText,
        onClick: handleButtonOne,
        loading: isRetrying,
        disabled: isRetrying
      }}
      buttonTwo={config.buttonTwo ? {
        text: buttonTwoText || "",
        onClick: handleButtonTwo,
        disabled: isRetrying
      } : undefined}
      showCloseButton={status === "success"}
    />
  );
};
